import { memo, useCallback } from "react";
import clsx from "clsx";
import { v4 as uuid } from "uuid";
import { useAlert } from "react-alert";

import { makeStyles } from "@material-ui/styles";

import {
  Card,
  CardHeader,
  Divider,
  CardContent,
  Button,
  Grid,
  TextField,
  colors,
} from "@material-ui/core";
import { alpha } from "@material-ui/core/styles/colorManipulator";

import AllowedPropertyList from "./AllowedPropertyList";

const useEditorStyles = makeStyles((theme) => ({
  createRuleButton: {
    color: theme.palette.common.white,
    backgroundColor: colors.green[600],
    "&:hover": {
      backgroundColor: colors.green[900],
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        backgroundColor: colors.green[600],
      },
    },
    "&:disabled": {
      backgroundColor: colors.grey[600],
    },
  },
  updateRuleButton: {
    color: colors.red[600],
    border: `1px solid ${alpha(colors.red[600], 0.5)}`,
    "&:hover": {
      border: `1px solid ${colors.red[600]}`,
      backgroundColor: alpha(
        colors.red[600],
        theme.palette.action.hoverOpacity
      ),
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
  },
  actionsContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  hidden: {
    visibility: "hidden",
  },
}));

const shrink = { shrink: true };

const editorStateToRule = ({
  id,
  name,
  url_regex,
  description,
  allowed_properties,
}) => {
  id = id || uuid();
  name = (name || "").trim();
  url_regex = (url_regex || "").trim();
  description = (description || "").trim();
  allowed_properties = (allowed_properties || [])
    .map((value) => value.trim())
    .filter((value) => !!value);
  return {
    id,
    name,
    url_regex,
    description,
    allowed_properties,
  };
};

function RulesEditor({
  editorType,

  unsavedEdits,
  setUnsavedEdits,

  editorState,
  setEditorState,

  onSave,
  onDelete,
}) {
  const classes = useEditorStyles();
  const alert = useAlert();

  const handlePropertyChange = useCallback(
    (prop, value) => {
      const newEditorState = {
        ...editorState,
        [prop]: value,
      };
      setEditorState(newEditorState);
      setUnsavedEdits(true);
    },
    [setUnsavedEdits, editorState, setEditorState]
  );

  const setName = useCallback(
    (e) => {
      handlePropertyChange("name", e.target.value);
    },
    [handlePropertyChange]
  );

  const setUrlRegex = useCallback(
    (e) => {
      handlePropertyChange("url_regex", e.target.value);
    },
    [handlePropertyChange]
  );

  const setDescription = useCallback(
    (e) => {
      handlePropertyChange("description", e.target.value);
    },
    [handlePropertyChange]
  );

  const setAllowedProperties = useCallback(
    (allowedProperties) => {
      handlePropertyChange("allowed_properties", allowedProperties);
    },
    [handlePropertyChange]
  );

  const handleSave = useCallback(() => {
    const rule = editorStateToRule(editorState);
    if (!rule.name) {
      alert.error("Rule must have a Name");
      return;
    }
    if (!rule.url_regex) {
      alert.error("Rule must have a URL Regex");
      return;
    }
    try {
      new RegExp(rule.url_regex);
    } catch {
      alert.error("URL Regex must be a valid regular expression");
      return;
    }
    if (rule.allowed_properties.length === 0) {
      alert.error("Rule must have at least one allowed property");
      return;
    }

    onSave(rule);
  }, [editorState, onSave]);

  const handleDelete = useCallback(() => {
    onDelete(editorState.id);
  }, [editorState, onDelete]);

  return (
    <Card>
      <CardHeader
        title={
          (editorType === "create" ? "Create A New Rule" : "Edit Rule") +
          (unsavedEdits ? " (Unsaved Changes)" : "")
        }
        action={
          <div aria-label="info" className="text-right text-xs text-grey-600">
            {editorState.created_at && (
              <>
                <span className="font-bold mr-2">Created:</span>
                {new Date(editorState.created_at).toLocaleString()}
                <br />
              </>
            )}

            {editorState.updated_at && (
              <>
                <span className="font-bold mr-2">Updated:</span>
                {new Date(editorState.updated_at).toLocaleString()}
              </>
            )}
          </div>
        }
      />

      <Divider />

      <CardContent>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              autoComplete="off"
              InputLabelProps={shrink}
              variant="outlined"
              label="Rule Name"
              name="name"
              value={editorState.name || ""}
              onChange={setName}
            />
          </Grid>

          <Grid item xs={12}>
            {/* TODO: make this autocompletable for better UI */}
            <TextField
              fullWidth
              autoComplete="off"
              InputLabelProps={shrink}
              variant="outlined"
              label="URL Regex"
              name="name"
              value={editorState.url_regex || ""}
              onChange={setUrlRegex}
              placeholder={"Example: ^https?:\\/\\/api\\.yoursite\\.com\\/api"}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              autoComplete="off"
              InputLabelProps={shrink}
              variant="outlined"
              multiline
              minRows={2}
              maxRows={5}
              label="Description (optional)"
              placeholder="Add a description for this network capture allowlist rule"
              name="description"
              value={editorState.description || ""}
              onChange={setDescription}
            />
          </Grid>
        </Grid>
      </CardContent>

      <Divider />

      <CardContent>
        <AllowedPropertyList
          values={editorState.allowed_properties || []}
          onUpdate={setAllowedProperties}
        />
      </CardContent>

      <Divider />

      <CardContent className={classes.actionsContainer}>
        <Button
          className={clsx(
            classes.updateRuleButton,
            editorType === "create" && classes.hidden
          )}
          onClick={handleDelete}
        >
          Delete
        </Button>

        <Button
          className={classes.createRuleButton}
          onClick={handleSave}
          variant="outlined"
        >
          {editorType === "create"
            ? "Create API Allowlist Rule"
            : "Save API Allowlist Rule"}
        </Button>
      </CardContent>
    </Card>
  );
}

const RulesEditorMemo = memo(RulesEditor);

export default RulesEditorMemo;
